import {
  SCREENSHOT_INNER_CONTAINER_ID,
  createXPathFromElement,
  getTargetElement,
  getVerticallyScrollableParentElement,
  runAfterMotion,
} from '@carboncopyco/mimesis-shared';
import {
  FrameType,
  type RelativePositionAlignment,
  RelativePositionAlignmentEnums,
  RelativePositionTypeEnums,
  TOUR_TOOLTIP_AREA_ELEMENT_ID,
  TOUR_TOOLTIP_ELEMENT_ID,
  type TourAspectRatio,
  type TourStep,
} from '@carboncopyco/types';
import scrollIntoView from 'scroll-into-view';

import { EventType, type eventWithTime } from '@carboncopyco/rrweb-types';
import { checkIsInViewport } from '@carboncopyco/ui';
import {
  SCREENSHOT_CONTAINER_THRESHOLD_DELTA,
  SEEK_TIME_OFFSET,
} from './constants';

const isElementOutOfScrollView = (
  element: HTMLElement,
  parentScrollable: HTMLElement | null
) => {
  const { top: elementTop, bottom: elementBottom } =
    element.getBoundingClientRect();
  if (parentScrollable) {
    const { top: parentTop, bottom: parentBottom } =
      parentScrollable.getBoundingClientRect();
    const isInViewport = elementTop > parentTop && elementBottom < parentBottom;
    return !isInViewport;
  }
  return false;
};

const navigateToScreenshotStep = ({
  step,
  iframeDocument,
}: {
  step: TourStep;
  iframeDocument: Document | undefined;
}) => {
  const tooltip = iframeDocument.getElementById(TOUR_TOOLTIP_ELEMENT_ID);
  const referenceElement = iframeDocument.getElementById(
    TOUR_TOOLTIP_AREA_ELEMENT_ID
  );
  const screenshotContainer = iframeDocument.getElementById(
    SCREENSHOT_INNER_CONTAINER_ID
  );

  if (!tooltip || !referenceElement) {
    return;
  }

  const scrollableParent =
    getVerticallyScrollableParentElement(referenceElement);

  const screenshotImage = screenshotContainer?.children?.[0];

  if (screenshotImage) {
    const { height: screenshotContainerHeight } =
      screenshotContainer.getBoundingClientRect();
    const { height: screenshotHeight } =
      screenshotImage.getBoundingClientRect();

    const screenshotContainerHeightThreshold =
      screenshotContainerHeight * SCREENSHOT_CONTAINER_THRESHOLD_DELTA;

    const shouldScroll = screenshotHeight > screenshotContainerHeightThreshold;

    if (!shouldScroll) {
      return;
    }
  }

  runAfterMotion(createXPathFromElement(tooltip), () => {
    scrollToElement(
      referenceElement,
      tooltip,
      scrollableParent,
      step.config.tooltip.balloon.layout.alignment
    );
  });
};

export const scrollToTargetElement = ({
  step,
  iframeDocument,
  frameType,
}: {
  step: TourStep;
  iframeDocument: Document | undefined;
  frameType: FrameType;
}) => {
  const isPageAligned =
    step.config.tooltip.balloon.layout.relation ===
    RelativePositionTypeEnums.page;

  // screenshot frame
  if (iframeDocument && frameType === FrameType.screenshot) {
    navigateToScreenshotStep({ iframeDocument, step });
    return;
  }

  if (iframeDocument && step.xpath && !isPageAligned) {
    const { documentRoot, targetElement: referenceElement } = getTargetElement({
      xpath: step.xpath,
      elementPosition: step.elementPosition,
      frameType,
      doc: iframeDocument,
    });
    const tooltip =
      documentRoot.getElementById(TOUR_TOOLTIP_ELEMENT_ID) ||
      iframeDocument.getElementById(TOUR_TOOLTIP_ELEMENT_ID);
    if (!tooltip) {
      return;
    }

    const scrollableParent =
      getVerticallyScrollableParentElement(referenceElement);
    if (
      !(checkIsInViewport(tooltip) && checkIsInViewport(referenceElement)) ||
      isElementOutOfScrollView(referenceElement, scrollableParent)
    ) {
      const iframeContainer = documentRoot.defaultView
        .frameElement as HTMLIFrameElement;
      const { clientHeight } = iframeDocument.defaultView
        .frameElement as HTMLIFrameElement;
      const { top } = iframeContainer.getBoundingClientRect();
      const isIframeOutOfView = top < 0 || top > clientHeight;

      runAfterMotion(createXPathFromElement(tooltip), () => {
        /*
          Only scroll to the iframeContainer if the top offset is negative
          or top offset is larger than clientHeight of entire rrweb iframe
          indicating the iframe element is out of view
        */
        if (documentRoot !== iframeDocument && isIframeOutOfView) {
          scrollToElement(
            iframeContainer,
            tooltip,
            scrollableParent,
            step.config.tooltip.balloon.layout.alignment
          );
          return;
        }
        scrollToElement(
          referenceElement,
          tooltip,
          scrollableParent,
          step.config.tooltip.balloon.layout.alignment
        );
      });
    }
  }
};

export const getSeekTime = (time: number, event?: eventWithTime) => {
  const modifiedTime =
    time === 0 || event?.type === EventType.Meta
      ? time + SEEK_TIME_OFFSET
      : time;
  return modifiedTime;
};

export const getFirstEvent = (events: eventWithTime[]) => {
  return events
    .filter((event) => event.type === EventType.Meta)
    .sort((event1, event2) =>
      event1.timestamp > event2.timestamp ? 1 : -1
    )?.[0];
};

export const isValidAspectRatio = (aspectRatio: TourAspectRatio): boolean => {
  const aspectRatios: TourAspectRatio[] = ['fixedWidthAndHeight', 'responsive'];

  return aspectRatios.includes(aspectRatio);
};

const scrollToElement = (
  element: HTMLElement,
  tooltip: HTMLElement,
  _parent: HTMLElement,
  position: RelativePositionAlignment
) => {
  const tooltipHeight = tooltip.clientHeight;
  const tooltipWidth = tooltip.clientWidth;
  const align = {
    top: 0.5,
    left: 0.5,
    topOffset: 0,
    leftOffset: 0,
  };

  switch (position) {
    case RelativePositionAlignmentEnums.topCenter:
      align.top = 0;
      align.topOffset = tooltipHeight;
      break;
    case RelativePositionAlignmentEnums.bottomCenter:
      align.top = 1;
      align.topOffset = -tooltipHeight;
      break;
    case RelativePositionAlignmentEnums.middleStart:
      align.left = 0;
      align.leftOffset = tooltipWidth;
      break;
    case RelativePositionAlignmentEnums.middleEnd:
      align.left = 1;
      align.leftOffset = -tooltipWidth;
      break;
  }

  scrollIntoView(element, {
    align,
    cancellable: false,
    // By default scroll-into-view will only attempt to scroll elements that have overflow
    // not set to `"hidden"` and who's scroll width/height is larger than their client height.
    isScrollable: () => true,
  });
};
